import React from "react";
import { useLang } from "../LangContext";
import { useReveal } from "../hooks/useReveal";
import { Kicker } from "./Mission";
import { Package, Eye, Layers } from "lucide-react";
import { BOX_RECTO_URL, BOX_VERSO_URL } from "../assets/brand";

export const BoxShowcase = () => {
  const { t } = useLang();
  const ref = useReveal();
  const b = t.followMe.box;

  return (
    <section
      id="box"
      data-testid="box-showcase-section"
      className="relative py-24 lg:py-32 overflow-hidden reveal"
      ref={ref}
    >
      {/* Subtle backdrop */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(800px_500px_at_15%_30%,rgba(255,107,0,0.15),transparent_60%),radial-gradient(700px_500px_at_85%_70%,rgba(139,92,246,0.18),transparent_60%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left — text */}
          <div className="lg:col-span-5">
            <Kicker label={b.kicker} />
            <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
              {b.title1}{" "}
              <span className="text-baj-grad">{b.title2}</span>
            </h2>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              {b.text}
            </p>

            <ul className="mt-8 space-y-4">
              {b.points.map((p, i) => {
                const Icon = [Package, Layers, Eye][i];
                return (
                  <li key={i} className="flex items-start gap-4">
                    <div className="shrink-0 w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-fuchsia-300" strokeWidth={2.5} />
                    </div>
                    <div>
                      <div className="font-display text-base font-bold text-white">
                        {p.title}
                      </div>
                      <div className="mt-0.5 text-sm text-slate-400">
                        {p.sub}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Right — two boxes side by side */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <BoxFigure
                src={BOX_RECTO_URL}
                label={b.recto}
                badge={b.frontBadge}
                testId="box-recto"
                glow="rgba(217,70,239,0.55)"
                rotate="-2deg"
              />
              <BoxFigure
                src={BOX_VERSO_URL}
                label={b.verso}
                badge={b.backBadge}
                testId="box-verso"
                glow="rgba(255,107,0,0.55)"
                rotate="2deg"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const BoxFigure = ({ src, label, badge, testId, glow, rotate }) => (
  <figure
    className="group relative"
    data-testid={testId}
    style={{ transform: `rotate(${rotate})` }}
  >
    <div className="relative aspect-[3/5] rounded-[1.5rem] overflow-hidden bg-white ring-1 ring-white/15 transition-all duration-500 group-hover:-translate-y-2 group-hover:rotate-0"
      style={{ boxShadow: `0 25px 60px -15px ${glow}` }}
    >
      <img
        src={src}
        alt={label}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        loading="lazy"
      />
      {/* Top badge */}
      <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/70 backdrop-blur px-2.5 py-1 text-[9px] uppercase tracking-[0.2em] font-bold text-white border border-white/15">
        {badge}
      </div>
    </div>
    <figcaption className="mt-3 text-center text-sm font-semibold text-slate-300">
      {label}
    </figcaption>
  </figure>
);
